/**
 * AbhaVerifiedResult — result panel after an ABHA verification (VRFY_ABHA_404, R23, R29).
 *
 * Shows the full profile (full ABHA number + address, R23) and View / Download ABHA card.
 * The card is the verify-session card (downloadVerifiedABHACard, never the stored-card route);
 * the session lives ~5 min, so it is fetched on click (single-flight), not on render.
 * CARD_NOT_AVAILABLE shows its plain message with a "Verify again" action.
 */
import React, { useRef, useState } from 'react';

import PrimaryButton from '../auth/PrimaryButton';
import useFileViewerStore from '../../stores/useFileViewerStore';
import { downloadVerifiedABHACard } from '../../services/api/abhaApi';
import { REGISTER_OPD, ADMIT_IPD } from '../../constants/abdmCopy';

const CARD_FAILED_MESSAGE = 'Could not load the ABHA card. Please try again.';

export default function AbhaVerifiedResult({ snapshot, txnId, isDark, onContinue, onAdmit, onStartOver }) {
  const inFlight = useRef(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardError, setCardError] = useState(null);   // { message, canVerifyAgain }

  const fetchCard = () => {
    if (!inFlight.current) {
      setCardBusy(true);
      setCardError(null);
      inFlight.current = downloadVerifiedABHACard({ txnId }).finally(() => {
        inFlight.current = null;
        setCardBusy(false);
      });
    }
    return inFlight.current;
  };

  const run = async (use) => {
    try {
      const { objectUrl } = await fetchCard();
      use(objectUrl);
    } catch (err) {
      const gone = err?.abhaCode === 'CARD_NOT_AVAILABLE';
      setCardError({ message: gone ? (err.userMessage || err.message) : CARD_FAILED_MESSAGE, canVerifyAgain: gone });
    }
  };

  const viewCard = () => run((blobUrl) => useFileViewerStore.getState().openViewer({
    blobUrl, fileType: 'image', title: 'ABHA Card',
  }));

  const downloadCard = () => run((href) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = 'ABHA-Card.png';
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 0);
  });

  const muted = isDark ? '#94a3b8' : '#6b7280';
  const btn = {
    padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#fff',
    color: isDark ? '#e2e8f0' : '#334155',
  };
  const photo = snapshot.profilePhoto ?? snapshot.profile_photo ?? snapshot.photo;
  const photoSrc = photo ? (String(photo).startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`) : null;
  const dob = snapshot.dob ?? snapshot.dateOfBirth ?? snapshot.date_of_birth;
  const address = snapshot.addressLine ?? snapshot.address;
  const rows = [
    ['Name', snapshot.fullName],
    dob ? ['Date of birth', dob] : ['Year of birth', snapshot.yearOfBirth],
    ['Gender', snapshot.gender],
    ['Mobile', snapshot.mobile],
    ['Address', address],
    ['State', snapshot.stateName],
    ['District', snapshot.districtName],
    ['ABHA number', snapshot.abhaNumber],
    ['ABHA address', snapshot.abhaAddress],
  ].map(([label, v]) => [label, v || '—']);

  return (
    <div
      data-testid="abha-result-panel"
      style={{
        borderRadius: 10, padding: '16px 18px', marginBottom: 16,
        border: `1px solid ${isDark ? '#065f46' : '#6ee7b7'}`,
        background: isDark ? 'rgba(6,78,59,0.4)' : '#ecfdf5',
      }}
    >
      <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#6ee7b7' : '#065f46' }}>
        ABHA verified
      </p>
      {photoSrc && (
        <img src={photoSrc} alt="ABHA profile" data-testid="abha-profile-photo"
          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} />
      )}
      {snapshot.abhaNumber ? (
        <dl style={{ margin: '8px 0 0', fontSize: 13 }}>
          {rows.map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <dt style={{ color: muted, minWidth: 100 }}>{label}</dt>
              <dd style={{ margin: 0, fontFamily: label.startsWith('ABHA') ? 'monospace' : 'inherit' }}>{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: muted }}>Loading profile…</p>
      )}

      {cardError && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: isDark ? '#fca5a5' : '#b91c1c' }}>
          {cardError.message}
          {cardError.canVerifyAgain && (
            <button type="button" onClick={onStartOver} style={{ ...btn, marginLeft: 10 }}>Verify again</button>
          )}
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <PrimaryButton onClick={onContinue} disabled={!snapshot.abhaNumber}>
          {REGISTER_OPD}
        </PrimaryButton>
        {onAdmit && (
          <PrimaryButton onClick={onAdmit} disabled={!snapshot.abhaNumber}>
            {ADMIT_IPD}
          </PrimaryButton>
        )}
        <button type="button" onClick={viewCard} disabled={cardBusy || !txnId} style={btn}>View ABHA card</button>
        <button type="button" onClick={downloadCard} disabled={cardBusy || !txnId} style={btn}>Download ABHA card</button>
        <button type="button" onClick={onStartOver} style={btn}>Start over</button>
      </div>
    </div>
  );
}
